/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SUBSCRIPTION OPERATIONS - Feature Gating & Limits
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Manages subscription tiers, features, and usage limits:
 * - Get user subscription info (tier, features, usage)
 * - Check feature access (boolean flags)
 * - Check creation limits (invoices, products, customers)
 * - Get current usage stats
 *
 * USAGE IN UI:
 * 1. On login: getUserSubscription() → Display tier & limits
 * 2. Before create: canCreateInvoice() → Check if allowed
 * 3. On dashboard refresh: getCurrentUsage() → Update counts
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase} from "../core/supabase.js";

// ══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION INFO
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get complete subscription details
 * Call on login to populate dashboard and UI state
 *
 * @returns {Promise<object>} Subscription with tier, features, and usage
 */
export async function getUserSubscription() {
  try {
    // Get tier + subscription data
    const { data: tierData, error: tierError } = await supabase.rpc('get_user_subscription');
    
    if (tierError) throw tierError;
    
    // Get usage counts separately
    const usageData = await getCurrentUsage();
    
    // Extract limits from features JSONB (single source of truth)
    const features = tierData.features || {};
    const invoiceLimit = features.max_invoices_per_month ? parseInt(features.max_invoices_per_month) : null;
    const productLimit = features.max_products ? parseInt(features.max_products) : null;
    const customerLimit = features.max_customers ? parseInt(features.max_customers) : null;
    
    // Return merged subscription + usage
    return {
      tierId: tierData.tier_id,
      tierName: tierData.tier_name,
      tierLevel: tierData.tier_level,
      monthlyPrice: tierData.monthly_price,
      features: features,
      status: tierData.status,
      currentPeriodStart: tierData.current_period_start,
      currentPeriodEnd: tierData.current_period_end,
      billingPeriodEnd: tierData.billing_period_end,
      usage: {
        invoicesThisMonth: usageData.invoicesThisMonth,
        invoiceLimit: invoiceLimit,
        productsTotal: usageData.productsTotal,
        productLimit: productLimit,
        customersTotal: usageData.customersTotal,
        customerLimit: customerLimit
      }
    };
  } catch (error) {
    console.error('Failed to get subscription:', error);
    throw new Error(`Failed to load subscription: ${error.message}`);
  }
}

/**
 * Check if user has access to a specific feature
 * 
 * @param {string} featureName - Feature key (e.g., 'invoice_export')
 * @returns {Promise<boolean>} True if user has feature
 */
export async function hasFeature(featureName) {
  try {
    const { data, error } = await supabase.rpc('user_has_feature', {
      p_feature_name: featureName
    });
    
    if (error) throw error;
    
    return data === true;
  } catch (error) {
    console.error(`Failed to check feature ${featureName}:`, error);
    return false; // Fail closed - deny access on error
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// LIMIT CHECKING (Call Before Create Operations)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user can create invoice
 * MUST call this before createInvoice()
 *
 * @returns {Promise<object>} { allowed, current, limit, remaining, message? }
 */
export async function canCreateInvoice() {
  try {
    const { data, error } = await supabase.rpc('can_create_invoice');
    
    if (error) throw error;
    
    return {
      allowed: data.allowed,
      current: data.current,
      limit: data.limit,
      remaining: data.remaining,
      unlimited: data.unlimited || false,
      billingPeriodEnd: data.billing_period_end,
      reason: data.reason,
      message: data.message,
      error: data.error
    };
  } catch (error) {
    console.error('Failed to check invoice limit:', error);
    
    // Fail closed - deny creation on error
    return {
      allowed: false,
      error: error.message,
      message: 'Failed to verify limits. Please try again.'
    };
  }
}

/**
 * Check if user can create product
 * MUST call this before createProduct()
 *
 * @returns {Promise<object>} { allowed, current, limit, remaining, message? }
 */
export async function canCreateProduct() {
  try {
    const { data, error } = await supabase.rpc('can_create_product');
    
    if (error) throw error;
    
    return {
      allowed: data.allowed,
      current: data.current,
      limit: data.limit,
      remaining: data.remaining,
      unlimited: data.unlimited || false,
      reason: data.reason,
      message: data.message,
      error: data.error
    };
  } catch (error) {
    console.error('Failed to check product limit:', error);
    
    return {
      allowed: false,
      error: error.message,
      message: 'Failed to verify limits. Please try again.'
    };
  }
}

/**
 * Check if user can create customer
 * MUST call this before createCustomer()
 *
 * @returns {Promise<object>} { allowed, current, limit, remaining, message? }
 */
export async function canCreateCustomer() {
  try {
    const { data, error } = await supabase.rpc('can_create_customer');
    
    if (error) throw error;
    
    return {
      allowed: data.allowed,
      current: data.current,
      limit: data.limit,
      remaining: data.remaining,
      unlimited: data.unlimited || false,
      reason: data.reason,
      message: data.message,
      error: data.error
    };
  } catch (error) {
    console.error('Failed to check customer limit:', error);
    
    return {
      allowed: false,
      error: error.message,
      message: 'Failed to verify limits. Please try again.'
    };
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// USAGE STATS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get current usage counts (for dashboard refresh)
 * Lighter than getUserSubscription() - only returns counts
 *
 * @returns {Promise<object>} { invoicesThisMonth, productsTotal, customersTotal }
 */
export async function getCurrentUsage() {
  try {
    const { data, error } = await supabase.rpc('get_current_usage');
    
    if (error) throw error;
    
    return {
      invoicesThisMonth: data.invoices_this_month,
      productsTotal: data.products_total,
      customersTotal: data.customers_total
    };
  } catch (error) {
    console.error('Failed to get current usage:', error);
    
    return {
      invoicesThisMonth: 0,
      productsTotal: 0,
      customersTotal: 0
    };
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED FUNCTIONS (7):
 *
 * Subscription Info:
 * - getUserSubscription() - Full subscription details (call on login)
 * - hasFeature(name) - Boolean feature check
 *
 * Limit Checking (call BEFORE create):
 * - canCreateInvoice() - Check monthly invoice limit
 * - canCreateProduct() - Check lifetime product limit
 * - canCreateCustomer() - Check lifetime customer limit
 *
 * Usage Stats:
 * - getCurrentUsage() - Quick usage refresh
 *
 * USAGE EXAMPLE:
 * 
 * // On login
 * const subscription = await getUserSubscription();
 * 
 * // Before creating invoice
 * const check = await canCreateInvoice();
 * if (!check.allowed) {
 *   showUpgradeModal(check.message);
 *   return;
 * }
 * await createInvoice(data);
 * 
 * // Check feature
 * if (await hasFeature('invoice_export')) {
 *   showExportButton();
 * }
 */